/* src/components/pages/Estadisticaventascli.jsx - Modificado el 20-01-2026 */
/* ------------- consulta estadística de ventas clientes ------------------ */

import React, {
  useEffect,
  useState,
  useCallback
} from 'react';

import {
  Box,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';

import api from '../../services/api';

import { useAuth } from '../../context/AuthContext';

import { useFeedback } from '../../context/FeedbackContext';

import NoData from './NoData';

import { normalizeRut } from "../utils/rutUtils";

const meses = [
  "",
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic"];

/* ================== Card reutilizable ================== */
const ChartCard = ({ title, subtitle, data, color }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  return (
    <Card sx={{ flex: '1 1 32%', minWidth: 320, borderTop: `3px solid ${ui.titleAccent}` }}>
      <CardContent>
        <Typography sx={ui.cardTitleAccent}>{title}</Typography>
        <Typography sx={ui.cardSubtitle}>{subtitle}</Typography>

        {data?.length ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={ui.chartGrid} vertical={false} />
              <XAxis
                dataKey="mes"
                tickFormatter={(m) => meses[m]}
                tick={{ fill: ui.chartTick, fontSize: 12 }}
              />
              <YAxis
                tick={{ fill: ui.chartTick, fontSize: 12 }}
                tickFormatter={(v) =>
                  new Intl.NumberFormat('es-CL', {
                    notation: 'compact',
                    compactDisplay: 'short'
                  }).format(v)
                }
              />
              <Tooltip
                labelFormatter={(label) => `Mes: ${meses[label]}`}
                formatter={(value) => `$${Number(value).toLocaleString('es-CL')}`}
              />
              <Line
                type="monotone"
                dataKey="total"
                stroke={color}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <NoData mensaje="No hay datos" />
        )}
      </CardContent>
    </Card>
  );
};

/* ================== Componente principal ================== */
const Estadisticaventascli = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();
  const theme = useTheme();
  const ui = theme.custom.ui;

  const [anos, setAnos] = useState([]);
  const [anoSeleccionado, setAnoSeleccionado] = useState('');
  const [payload, setPayload] = useState(null);

  /* ===== Cargar años ===== */
  const fetchAnos = useCallback(async () => {
    try {
      const res = await api.post('/libroventas/getanosventas', {
        RutCli: user.usuario,
        RutEnr: '',
        Tipousr: 1, /* cliente */
      });

      if (res.data?.rows?.length) {
        setAnos(res.data.rows);
        setAnoSeleccionado(res.data.rows[0].ano);
      }
    } catch {
      showFeedback({
        message: 'No fue posible obtener los años disponibles',
        severity: 'error'
      });
    }
  }, [user.usuario, showFeedback]);

  useEffect(() => {
    fetchAnos();
  }, [fetchAnos]);

  /* ===== Cargar estadísticas ===== */
  const fetchData = useCallback(async () => {
    if (!anoSeleccionado) return;

    try {
      const res = await api.post('/libroventas/getestadisticaventas', {
        RutCli: normalizeRut(user.usuario),
        RutEnr: '',
        Tipousr: 1, /* Cliente */
        Anoaconsultar: anoSeleccionado,
      });

      if (res.data?.success) {
        setPayload(res.data);
      }
    } catch {
      showFeedback({
        message: 'No fue posible obtener estadísticas',
        severity: 'error'
      });
    }
  }, [anoSeleccionado, user.usuario, showFeedback]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!payload) return null;

  const { series, resumen } = payload;

  return (
    <Box>
      {/* Controles */}
      <Stack direction="row" spacing={1} mb={1}>
        <TextField
          label="Rut"
          value={user.usuario}
          size="small"
          disabled
          sx={ui.readonlyField}
        />
        <TextField
          label="Cliente"
          value={user.nombre}
          size="small"
          disabled
          sx={{ ...ui.readonlyField, flexGrow: 1 }}
        />
        <TextField
          select
          label="Año"
          value={anoSeleccionado}
          onChange={(e) => setAnoSeleccionado(e.target.value)}
          size="small"
          sx={{ width: 100 }}
        >
          {anos.map(({ ano }) => (
            <MenuItem key={ano} value={ano}>{ano}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <ChartCard title="Factura Electrónica (33)" subtitle={`Año ${anoSeleccionado}`} data={series.fa33} color={ui.primary} />
        <ChartCard title="Factura Exenta (34)" subtitle={`Año ${anoSeleccionado}`} data={series.fa34} color={ui.secondary} />
        <ChartCard title="Nota de Crédito (61)" subtitle={`Año ${anoSeleccionado}`} data={series.nc61} color={ui.error} />
        <ChartCard title="Nota de Débito (56)" subtitle={`Año ${anoSeleccionado}`} data={series.nd56} color={ui.warning} />
        <ChartCard title="Guía de Despacho (52)" subtitle={`Año ${anoSeleccionado}`} data={series.gd52} color={ui.info} />

        {/* Resumen */}
        <Card sx={{ flex: '1 1 32%', minWidth: 320, borderTop: `3px solid ${ui.titleAccent}` }}>
          <CardContent>
            <Typography sx={theme.cardTitleAccent}>
              Resumen anual
            </Typography>
            <Typography sx={ui.cardSubtitle}>
              Año {anoSeleccionado}
            </Typography>

            <Stack spacing={1.1} mt={2}>
              {/* Detalle por tipo de documento */}
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  Factura Electrónica (33)
                </Typography>
                <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>
                  ${resumen.totalFa33.toLocaleString('es-CL')}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  Factura Exenta Electrónica (34)
                </Typography>
                <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>
                  ${resumen.totalFa34.toLocaleString('es-CL')}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  Nota de Débito Electrónica (56)
                </Typography>
                <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>
                  ${resumen.totalNd56.toLocaleString('es-CL')}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  Nota de Crédito Electrónica (61)
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    textAlign: 'right',
                    color: resumen.totalNc61 > 0 ? 'error.main' : 'text.primary',
                  }}
                >
                  -${resumen.totalNc61.toLocaleString('es-CL')}
                </Typography>
              </Stack>

              {/* Separador visual */}
              <Box
                sx={{
                  borderTop: `1px solid ${ui.borderSubtle}`,
                  my: 1.5,
                }}
              />

              {/* Totales consolidados */}
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontWeight: 600 }}>
                  Venta Neta Total
                </Typography>
                <Typography sx={{ fontWeight: 700, textAlign: 'right' }}>
                  ${resumen.totalNeto.toLocaleString('es-CL')}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  Promedio mensual
                </Typography>
                <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>
                  ${resumen.promedioMensual.toLocaleString('es-CL')}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  Mes con mayor venta
                </Typography>
                <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>
                  {resumen.mesFuerte
                    ? `${resumen.mesFuerte.mes} $${resumen.mesFuerte.valor.toLocaleString('es-CL')}`
                    : '—'}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Estadisticaventascli;
